<template>
  <div>
    <div class="columns gutter">
      <div class="column">
        <b-notification
          v-if="isFeatureEnabled('sfx_upgrade_cta_sfx_page') && showSfxUpgradeCta"
          type="is-warning is-light"
        >
          It looks like you're on a legacy plan. <router-link :to="{ name: 'subscription' }">Click here to upgrade and access Sfx!</router-link>
        </b-notification>
        <div class="field">
          <div class="control">
            <div
              role="combobox"
            >
              <input
                id="search-input"
                v-model="searchText"
                type="text"
                class="input is-large search"
                placeholder="Search from over 250,000 sound effects, add more keywords to refine your search"
                autocomplete="off"
                @keyup.enter="handleAddTextSearch"
              >
            </div>
          </div>
        </div>
        <h3
          v-if="featuredSearchMode"
          class="title is-3 text mt-6"
        >
          Featured {{ featuredSearchQuery }} Search
        </h3>
        <div
          v-if="results.length > 0 && !loadingNewSearch"
          class="is-fullwidth"
        >
          <p class="text large mb-4">
            {{ totalCount|formattedNumber }} results
          </p>
          <sfx-list-item
            v-for="sfx in results"
            :key="sfx.id"
            :sfx="sfx"
          />
        </div>
        <div
          v-if="results.length === 0 && !loadingNewSearch"
          class="is-fullwidth"
        >
          <div class="container">
            <div class="columns is-vcentered mt-6">
              <div class="column is-6 is-offset-3 has-text-centered">
                <h3 class="title-wide is-1 text mb-5">
                  Looking for something specific?
                </h3>
                <p class="text large mb-4">
                  We couldn't find anything for your search. Try widening your search terms or using different keywords.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="loading"
          class="columns"
        >
          <div class="column is-12 has-text-centered">
            <br>
            <span
              class="loader xl"
              style="display: inline-block; width: 3em; height: 3em;"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- div.columns -->
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { formatComponentName } from '@/utils/common.js'
import SfxListItem from '@/components/SfxListItem.vue'

const MIN_SEARCH_TERM_LENGTH = 2

export default {
  name: 'SfxSearch',

  components: {
    SfxListItem
  },

  data () {
    return {
      searchText: '',
      featuredSearchMode: false,
      featuredSearchQuery: ''
    }
  },

  computed: {
    ...mapGetters({ isFeatureEnabled: 'waffle/isFeatureEnabled' }),
    ...mapGetters('sfxSearch', [
      'searchQuery',
      'loading',
      'loadingNewSearch'
    ]),
    ...mapState('sfxSearch', ['results', 'totalCount']),

    isLoggedIn: function () {
      return this.$store.getters['auth/isLoggedIn']
    },

    showSfxUpgradeCta: function () {
      return this.$store.getters['subscriptions/upgradeablePlans'].length > 0
    },

    componentName: function () {
      return formatComponentName(this)
    }
  },

  created () {
    window.addEventListener('scroll', this.handleScroll)
  },

  mounted () {
    this.$store.dispatch('sfxSearch/resetSearch')
    this.initFeaturedSearch()

    this.$store.dispatch('waffle/fetch').then(() => {
      if (this.isFeatureEnabled('sfx_plan_upgrade') && this.isFeatureEnabled('sfx_upgrade_cta_sfx_page')) {
        if (this.isLoggedIn) {
          this.$store.dispatch('subscriptions/fetchUpgradeOptions')
        }
      }
    })
  },

  destroyed () {
    window.removeEventListener('scroll', this.handleScroll)
  },

  methods: {
    newSearch: function (searchQuery = this.searchText, featuredSearchMode = false) {
      this.$store.dispatch('sfxSearch/setSearchQuery', searchQuery)
      this.$store.dispatch('sfxSearch/setContinuationToken', null)
      this.$store.dispatch('sfxSearch/setLoadingNewSearch', true)
      this.doSearch()

      this.featuredSearchMode = featuredSearchMode
      if (featuredSearchMode) {
        this.featuredSearchQuery = searchQuery
      } else {
        this.featuredSearchQuery = ''
      }
    },

    doSearch: function () {
      this.$store.dispatch('sfxSearch/setLoading', true)
      this.$store.dispatch('sfxSearch/doSearch')
        .then(() => {
          this.$store.dispatch('sfxSearch/setLoadingNewSearch', false)
          this.$store.dispatch('sfxSearch/setLoading', false)
        })
    },

    initFeaturedSearch: function () {
      const featuredSearchTerms = [
        'alien', 'bird', 'cafe', 'car', 'cat', 'dog', 'door creak', 'explosion',
        'footsteps', 'lion', 'office', 'rain', 'restaurant', 'thunder', 'ufo', 'water'
      ]
      const randomIndex = Math.floor(Math.random() * featuredSearchTerms.length)
      this.newSearch(featuredSearchTerms[randomIndex], true)
    },

    handleAddTextSearch: function (event) {
      event.preventDefault()
      if (this.searchText.length >= MIN_SEARCH_TERM_LENGTH) {
        this.newSearch()
      }
    },

    handleScroll: function (event) {
      // Infinite scroll search results.
      // Don't bother calculating if we're at the bottom if there isn't a next page.
      if (this.$store.state.sfxSearch.continuationToken !== null) {
        const bottomOfWindow = (
          Math.round(document.documentElement.scrollTop) + window.innerHeight >=
          document.documentElement.offsetHeight
        )
        if (bottomOfWindow && !this.loading) {
          this.doSearch()
        }
      }
    }
  },

  metaInfo () {
    let url = window.location.origin
    const path = this.$route.path
    if (path !== '/') {
      url += path
    }
    const title = 'Browse & Download Sound Effects'
    const description = 'Discover and license the best sfx for your projects.'

    return {
      title,
      meta: [
        { name: 'description', content: description },
        { property: 'og:url', content: url },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:image', content: '/img/sonata-meta.png' },
        { name: 'twitter:card', content: 'summary' }
      ]
    }
  }
}
</script>

<style lang="scss">
@import "../assets/scss/_variables.scss";

#search-input {
  caret-color: $bluegrey5;
  color: $bluegrey0;
  position: relative;
  display: block;
  width: 100%;
  box-sizing: border-box;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
}
</style>
